import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import Euro from "@mui/icons-material/Euro";
import type { TextFieldProps } from "@mui/material/TextField";
import React, { type JSX } from "react";

import type { ErrorMap, BoundedValue } from "@repo/shared/common";
import { LocalizedNumberField } from "./LocalizedNumberField";
import type { RoundingMode } from "../common/numberUtils";

import { areBoundFieldPropsEqual } from "./MemoHelper";

export type PureBoundNumberFieldProps = {
	icon?: JSX.Element;
	iconBackgroundColor?: string;
	value: number | null;
	onChangeValue: (value: number | null) => void;

	label: string;
	errors?: ErrorMap;
	errorKey?: string;

	// Formatting options
	decimalScale?: number;
	fixedDecimalScale?: boolean;
	allowNegative?: boolean;
	showThousandSeparator?: boolean;
	decimalSeparator?: string;
	thousandSeparator?: string;

	// Rounding options
	roundingMode?: RoundingMode;

	additionalProps?: Omit<TextFieldProps, "value" | "onChange" | "label" | "error" | "helperText">;
};

const PureBoundNumberField = ({
	icon,
	iconBackgroundColor,
	value,
	onChangeValue,
	label,
	errors,
	errorKey,
	decimalScale,
	fixedDecimalScale,
	allowNegative,
	showThousandSeparator,
	decimalSeparator,
	thousandSeparator,
	roundingMode,
	additionalProps,
}: PureBoundNumberFieldProps): JSX.Element => {
	const key = errorKey;
	const errorMessage = key != null ? errors?.[key] : undefined;

	return (
		<LocalizedNumberField
			value={value}
			onValueChange={onChangeValue}
			label={label}
			error={Boolean(errorMessage)}
			helperText={errorMessage}
			icon={icon}
			iconBackgroundColor={iconBackgroundColor}
			decimalScale={decimalScale}
			fixedDecimalScale={fixedDecimalScale}
			allowNegative={allowNegative}
			showThousandSeparator={showThousandSeparator}
			decimalSeparator={decimalSeparator}
			thousandSeparator={thousandSeparator}
			roundingMode={roundingMode}
			additionalProps={additionalProps}
		/>
	);
};

// Memoized version: re-render only if relevant props changed
const MemoBoundNumberField = React.memo(PureBoundNumberField, areBoundFieldPropsEqual);

export type BoundNumberFieldProps = {
	boundedValue: BoundedValue<number | null>;
	icon?: JSX.Element;
	iconBackgroundColor?: string;
	label: string;

	// Formatting options
	decimalScale?: number;
	fixedDecimalScale?: boolean;
	allowNegative?: boolean;
	showThousandSeparator?: boolean;
	decimalSeparator?: string;
	thousandSeparator?: string;

	// Rounding options
	roundingMode?: RoundingMode;

	additionalProps?: Omit<TextFieldProps, "value" | "onChange" | "label" | "error" | "helperText">;
};

export function BoundNumberField({
	boundedValue,
	...props
}: BoundNumberFieldProps): JSX.Element {
	return (
		<MemoBoundNumberField
			{...props}
			value={boundedValue.value}
			onChangeValue={(v) => {
				boundedValue.onChangeValue(v);
			}}
			errorKey={boundedValue.errorKey}
			errors={boundedValue.errorMap}
		/>
	);
}

export type BoundEuroFieldProps = Omit<BoundNumberFieldProps, "icon" | "iconBackgroundColor" | "decimalScale" | "fixedDecimalScale" | "roundingMode">;

export const BoundEuroField = (props: BoundEuroFieldProps): JSX.Element => {
	return (
		<BoundNumberField
			{...props}
			icon={<Euro />}
			iconBackgroundColor="#1a455c"
			decimalScale={2}
			fixedDecimalScale={true}
			roundingMode="halfUp"
		/>
	);
};

export type BoundHoursFieldProps = Omit<BoundNumberFieldProps, "icon" | "iconBackgroundColor">;

export const BoundHoursField = (props: BoundHoursFieldProps): JSX.Element => {
	return (
		<BoundNumberField
			{...props}
			icon={<AccessAlarmIcon />}
			iconBackgroundColor="#1a455c"
		/>
	);
};
